import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaClipboardCheck,
  FaDownload,
  FaEdit,
  FaEye,
  FaQrcode,
  FaSearch,
  FaSpinner,
  FaSync,
  FaTrash,
  FaUsers,
} from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  EmptyStateTitle,
  Select,
} from '../../styles/AdminStyles';
import {
  BRANCHES,
  fetchAdminJson,
  formatCurrency,
  formatDate,
  getAdminHeaders,
  getParticipantCount,
  REGISTRATIONS_PER_PAGE,
} from './campAdminShared';

const PAYMENT_OPTIONS = ['pending', 'partial', 'completed'];

const CampRegistrationAdmin = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [paymentRegistration, setPaymentRegistration] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paymentStatus: 'pending',
    amountPaid: 0,
    paymentNotes: '',
  });

  const loadRegistrations = async () => {
    try {
      setError('');
      const data = await fetchAdminJson('/camp-registration/all', {
        headers: getAdminHeaders(),
      });
      setRegistrations(data);
    } catch (loadError) {
      console.error('Error loading registrations:', loadError);
      setError(loadError.message || 'Failed to load registrations.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadRegistrations();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterBranch]);

  const filteredRegistrations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return registrations.filter((registration) => {
      const searchableFields = [
        registration.fullName,
        registration.phone,
        registration.email,
        registration.registrationNumber,
        registration.spouseInfo?.name,
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());

      const matchesSearch =
        !term || searchableFields.some((value) => value.includes(term));
      const matchesStatus =
        filterStatus === 'all' || registration.paymentStatus === filterStatus;
      const matchesBranch =
        filterBranch === 'all' || registration.branch === filterBranch;

      return matchesSearch && matchesStatus && matchesBranch;
    });
  }, [registrations, searchTerm, filterStatus, filterBranch]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRegistrations.length / REGISTRATIONS_PER_PAGE),
  );

  const paginatedRegistrations = filteredRegistrations.slice(
    (currentPage - 1) * REGISTRATIONS_PER_PAGE,
    currentPage * REGISTRATIONS_PER_PAGE,
  );

  const filteredCamperCount = filteredRegistrations.reduce(
    (sum, registration) => sum + getParticipantCount(registration),
    0,
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadRegistrations();
  };

  const openPaymentModal = (registration) => {
    setPaymentRegistration(registration);
    setPaymentData({
      paymentStatus: registration.paymentStatus || 'pending',
      amountPaid: registration.amountPaid || 0,
      paymentNotes: registration.paymentNotes || '',
    });
  };

  const closePaymentModal = () => {
    setPaymentRegistration(null);
    setPaymentData({
      paymentStatus: 'pending',
      amountPaid: 0,
      paymentNotes: '',
    });
  };

  const handleSavePayment = async () => {
    if (!paymentRegistration) {
      return;
    }

    try {
      setSavingPayment(true);
      await fetchAdminJson(
        `/camp-registration/registration/${paymentRegistration._id}/payment`,
        {
          method: 'PATCH',
          headers: getAdminHeaders(true),
          body: JSON.stringify(paymentData),
        },
      );
      await loadRegistrations();
      closePaymentModal();
    } catch (saveError) {
      console.error('Error updating payment:', saveError);
      setError(saveError.message || 'Failed to update payment.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleCancelRegistration = async (registration) => {
    const shouldContinue = window.confirm(
      `Cancel registration ${registration.registrationNumber} for ${registration.fullName}?`,
    );

    if (!shouldContinue) {
      return;
    }

    try {
      await fetchAdminJson(
        `/camp-registration/registration/${registration._id}`,
        {
          method: 'DELETE',
          headers: getAdminHeaders(),
        },
      );
      if (selectedRegistration?._id === registration._id) {
        setSelectedRegistration(null);
      }
      await loadRegistrations();
    } catch (cancelError) {
      console.error('Error cancelling registration:', cancelError);
      setError(cancelError.message || 'Failed to cancel registration.');
    }
  };

  const exportCsv = () => {
    const rows = [
      [
        'Registration Number',
        'Full Name',
        'Email',
        'Phone',
        'Branch',
        'Room',
        'Participants',
        'Total Fee',
        'Amount Paid',
        'Balance',
        'Payment Status',
        'Created At',
      ],
      ...filteredRegistrations.map((registration) => {
        const totalFee = registration.feeBreakdown?.totalFee || 0;
        const amountPaid = registration.amountPaid || 0;

        return [
          registration.registrationNumber || '',
          registration.fullName || '',
          registration.email || '',
          registration.phone || '',
          registration.branch || '',
          registration.roomType || '',
          getParticipantCount(registration),
          totalFee,
          amountPaid,
          totalFee - amountPaid,
          registration.paymentStatus || '',
          formatDate(registration.createdAt),
        ];
      }),
    ];

    const csvContent = rows
      .map((row) =>
        row
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `camp-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <AdminLayout title="Camp Registrations" subtitle="Loading...">
        <LoadingState>
          <FaSpinner className="spin" />
          <p>Loading camp registrations...</p>
        </LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Camp Registrations"
      subtitle="Manage camper records and payments. QR codes, check-ins, and dashboard stats each live on their own page."
    >
      {error && (
        <Alert $type="error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </Alert>
      )}

      <NavCards>
        <NavCard to="/admin/camp-dashboard">
          <FaUsers />
          <div>
            <strong>Camp Dashboard</strong>
            <span>Stats and summary metrics</span>
          </div>
        </NavCard>

        <NavCard to="/admin/camp-qr-codes">
          <FaQrcode />
          <div>
            <strong>Camper QR Codes</strong>
            <span>Open and download QR codes</span>
          </div>
        </NavCard>

        <NavCard to="/admin/camp-checkins">
          <FaClipboardCheck />
          <div>
            <strong>Camp Check-Ins</strong>
            <span>Scan and review attendance</span>
          </div>
        </NavCard>
      </NavCards>

      <Card style={{ marginBottom: '1.5rem' }}>
        <CardBody>
          <Toolbar>
            <SearchBox>
              <FaSearch />
              <input
                type="text"
                placeholder="Search by name, phone, email, or registration number..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </SearchBox>

            <FilterRow>
              <Select
                value={filterBranch}
                onChange={(event) => setFilterBranch(event.target.value)}
              >
                <option value="all">All Branches</option>
                {BRANCHES.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </Select>

              <Select
                value={filterStatus}
                onChange={(event) => setFilterStatus(event.target.value)}
              >
                <option value="all">All Payment Statuses</option>
                {PAYMENT_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>

              <Button onClick={handleRefresh} disabled={refreshing}>
                <FaSync className={refreshing ? 'spin' : ''} /> Refresh
              </Button>

              <Button $variant="secondary" onClick={exportCsv}>
                <FaDownload /> Export CSV
              </Button>
            </FilterRow>
          </Toolbar>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <HeaderRow>
            <CardTitle>Registrations</CardTitle>
            <HeaderMeta>
              {filteredCamperCount} campers across {filteredRegistrations.length}{' '}
              registration forms
            </HeaderMeta>
          </HeaderRow>
        </CardHeader>
        <CardBody>
          {paginatedRegistrations.length > 0 ? (
            <>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>Reg #</th>
                      <th>Name</th>
                      <th>Branch</th>
                      <th>Phone</th>
                      <th>Room</th>
                      <th>Total Fee</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRegistrations.map((registration) => {
                      const totalFee = registration.feeBreakdown?.totalFee || 0;
                      const amountPaid = registration.amountPaid || 0;

                      return (
                        <tr key={registration._id}>
                          <td>{registration.registrationNumber}</td>
                          <td>
                            <RegistrationName>
                              <strong>{registration.fullName}</strong>
                              <ParticipantBadges>
                                <Badge $variant="info">
                                  <FaUsers /> {getParticipantCount(registration)}
                                </Badge>
                                {registration.hasSpouse &&
                                  registration.spouseInfo?.name && (
                                    <Badge $variant="info">Spouse</Badge>
                                  )}
                                {(registration.children?.length || 0) > 0 && (
                                  <Badge $variant="info">
                                    {registration.children.length} child
                                    {registration.children.length > 1 ? 'ren' : ''}
                                  </Badge>
                                )}
                              </ParticipantBadges>
                            </RegistrationName>
                          </td>
                          <td>{registration.branch || 'N/A'}</td>
                          <td>{registration.phone || 'N/A'}</td>
                          <td>{registration.roomType || 'N/A'}</td>
                          <td>{formatCurrency(totalFee)}</td>
                          <td>{formatCurrency(amountPaid)}</td>
                          <td>{formatCurrency(totalFee - amountPaid)}</td>
                          <td>
                            <StatusBadge $status={registration.paymentStatus}>
                              {registration.paymentStatus}
                            </StatusBadge>
                          </td>
                          <td>{formatDate(registration.createdAt)}</td>
                          <td>
                            <ActionRow>
                              <IconButton
                                type="button"
                                title="Update payment"
                                onClick={() => openPaymentModal(registration)}
                              >
                                <FaEdit />
                              </IconButton>

                              <IconButton
                                type="button"
                                title="View details"
                                onClick={() => setSelectedRegistration(registration)}
                              >
                                <FaEye />
                              </IconButton>

                              <IconButton
                                type="button"
                                $tone="danger"
                                title="Cancel registration"
                                onClick={() =>
                                  handleCancelRegistration(registration)
                                }
                              >
                                <FaTrash />
                              </IconButton>
                            </ActionRow>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>

              {totalPages > 1 && (
                <PaginationRow>
                  <Button
                    $variant="secondary"
                    onClick={() =>
                      setCurrentPage((page) => Math.max(page - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <PaginationText>
                    Page {currentPage} of {totalPages}
                  </PaginationText>
                  <Button
                    $variant="secondary"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(page + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </PaginationRow>
              )}
            </>
          ) : (
            <EmptyState>
              <EmptyStateIcon>
                <FaUsers />
              </EmptyStateIcon>
              <EmptyStateTitle>No registrations found</EmptyStateTitle>
              <EmptyStateText>
                Try adjusting your search or filter settings.
              </EmptyStateText>
            </EmptyState>
          )}
        </CardBody>
      </Card>

      <AnimatePresence>
        {selectedRegistration && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedRegistration(null)}
          >
            <ModalContent
              as={motion.div}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              onClick={(event) => event.stopPropagation()}
            >
              <ModalHeader>
                <div>
                  <h2>{selectedRegistration.fullName}</h2>
                  <p>{selectedRegistration.registrationNumber}</p>
                </div>
                <CloseButton
                  type="button"
                  onClick={() => setSelectedRegistration(null)}
                >
                  Close
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                <DetailsGrid>
                  <DetailCard>
                    <h3>Registration</h3>
                    <DetailRow>
                      <span>Branch</span>
                      <strong>{selectedRegistration.branch || 'N/A'}</strong>
                    </DetailRow>
                    <DetailRow>
                      <span>Phone</span>
                      <strong>{selectedRegistration.phone || 'N/A'}</strong>
                    </DetailRow>
                    <DetailRow>
                      <span>Email</span>
                      <strong>{selectedRegistration.email || 'N/A'}</strong>
                    </DetailRow>
                    <DetailRow>
                      <span>Room</span>
                      <strong>{selectedRegistration.roomType || 'N/A'}</strong>
                    </DetailRow>
                    <DetailRow>
                      <span>Registered</span>
                      <strong>{formatDate(selectedRegistration.createdAt)}</strong>
                    </DetailRow>
                  </DetailCard>

                  <DetailCard>
                    <h3>Payment</h3>
                    <DetailRow>
                      <span>Status</span>
                      <StatusBadge $status={selectedRegistration.paymentStatus}>
                        {selectedRegistration.paymentStatus}
                      </StatusBadge>
                    </DetailRow>
                    <DetailRow>
                      <span>Total Fee</span>
                      <strong>
                        {formatCurrency(
                          selectedRegistration.feeBreakdown?.totalFee,
                        )}
                      </strong>
                    </DetailRow>
                    <DetailRow>
                      <span>Amount Paid</span>
                      <strong>
                        {formatCurrency(selectedRegistration.amountPaid)}
                      </strong>
                    </DetailRow>
                    <DetailRow>
                      <span>Balance</span>
                      <strong>
                        {formatCurrency(
                          (selectedRegistration.feeBreakdown?.totalFee || 0) -
                            (selectedRegistration.amountPaid || 0),
                        )}
                      </strong>
                    </DetailRow>
                  </DetailCard>

                  <DetailCard>
                    <h3>Primary Camper</h3>
                    <DetailRow>
                      <span>Name</span>
                      <strong>{selectedRegistration.fullName}</strong>
                    </DetailRow>
                    <DetailRow>
                      <span>Gender</span>
                      <strong>{selectedRegistration.gender || 'N/A'}</strong>
                    </DetailRow>
                    <DetailRow>
                      <span>T-Shirt Size</span>
                      <strong>{selectedRegistration.tshirtSize || 'N/A'}</strong>
                    </DetailRow>
                    <DetailRow>
                      <span>T-Shirt Color</span>
                      <strong>
                        {selectedRegistration.tshirtColor || 'N/A'}
                      </strong>
                    </DetailRow>
                  </DetailCard>

                  <DetailCard>
                    <h3>Family</h3>
                    {selectedRegistration.hasSpouse &&
                    selectedRegistration.spouseInfo?.name ? (
                      <FamilyBlock>
                        <strong>Spouse</strong>
                        <span>{selectedRegistration.spouseInfo.name}</span>
                        <span>
                          {selectedRegistration.spouseInfo.phone || 'No phone'}
                        </span>
                      </FamilyBlock>
                    ) : (
                      <FamilyBlock>
                        <strong>No spouse added</strong>
                      </FamilyBlock>
                    )}

                    {(selectedRegistration.children?.length || 0) > 0 ? (
                      selectedRegistration.children.map((child, index) => (
                        <FamilyBlock key={`${child.name}-${index}`}>
                          <strong>Child {index + 1}</strong>
                          <span>{child.name}</span>
                          <span>{child.age ? `${child.age} years` : 'Age N/A'}</span>
                        </FamilyBlock>
                      ))
                    ) : (
                      <FamilyBlock>
                        <strong>No children added</strong>
                      </FamilyBlock>
                    )}
                  </DetailCard>
                </DetailsGrid>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {paymentRegistration && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePaymentModal}
          >
            <PaymentModal
              as={motion.div}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              onClick={(event) => event.stopPropagation()}
            >
              <ModalHeader>
                <div>
                  <h2>Update Payment</h2>
                  <p>{paymentRegistration.registrationNumber}</p>
                </div>
                <CloseButton type="button" onClick={closePaymentModal}>
                  Close
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                <FormStack>
                  <label htmlFor="payment-status">Payment Status</label>
                  <Select
                    id="payment-status"
                    value={paymentData.paymentStatus}
                    onChange={(event) =>
                      setPaymentData((current) => ({
                        ...current,
                        paymentStatus: event.target.value,
                      }))
                    }
                  >
                    {PAYMENT_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Select>
                </FormStack>

                <FormStack>
                  <label htmlFor="amount-paid">Amount Paid</label>
                  <Input
                    id="amount-paid"
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentData.amountPaid}
                    onChange={(event) =>
                      setPaymentData((current) => ({
                        ...current,
                        amountPaid: Number(event.target.value) || 0,
                      }))
                    }
                  />
                </FormStack>

                <FormStack>
                  <label htmlFor="payment-notes">Payment Notes</label>
                  <TextArea
                    id="payment-notes"
                    rows="4"
                    value={paymentData.paymentNotes}
                    onChange={(event) =>
                      setPaymentData((current) => ({
                        ...current,
                        paymentNotes: event.target.value,
                      }))
                    }
                  />
                </FormStack>

                <PaymentActions>
                  <Button $variant="secondary" onClick={closePaymentModal}>
                    Cancel
                  </Button>
                  <Button onClick={handleSavePayment} disabled={savingPayment}>
                    {savingPayment ? (
                      <>
                        <FaSpinner className="spin" /> Saving
                      </>
                    ) : (
                      'Save Payment'
                    )}
                  </Button>
                </PaymentActions>
              </ModalBody>
            </PaymentModal>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

const LoadingState = styled.div`
  min-height: 280px;
  display: grid;
  place-items: center;
  color: #64748b;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

const NavCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const NavCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.2rem;
  border-radius: 18px;
  text-decoration: none;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  color: #0f172a;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);

  svg {
    color: #2563eb;
    font-size: 1.4rem;
  }

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 0.25rem;
    color: #64748b;
    font-size: 0.92rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

const SearchBox = styled.div`
  min-width: min(100%, 360px);
  flex: 1 1 320px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  border: 1px solid #dbe2ea;
  background: white;

  svg {
    color: #64748b;
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 0.95rem;
    color: #0f172a;
    background: transparent;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
`;

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const HeaderMeta = styled.div`
  color: #64748b;
  font-size: 0.92rem;
  font-weight: 600;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1060px;

  th,
  td {
    text-align: left;
    padding: 0.95rem 0.8rem;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: top;
  }

  th {
    color: #475569;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  td {
    color: #0f172a;
    font-size: 0.94rem;
  }
`;

const RegistrationName = styled.div`
  strong {
    display: block;
  }
`;

const ParticipantBadges = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: capitalize;
  background: ${(props) => {
    if (props.$status === 'completed') return '#dcfce7';
    if (props.$status === 'partial') return '#fef3c7';
    return '#fee2e2';
  }};
  color: ${(props) => {
    if (props.$status === 'completed') return '#166534';
    if (props.$status === 'partial') return '#92400e';
    return '#991b1b';
  }};
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: ${(props) => (props.$tone === 'danger' ? '#fee2e2' : '#eff6ff')};
  color: ${(props) => (props.$tone === 'danger' ? '#b91c1c' : '#1d4ed8')};
  cursor: pointer;
`;

const PaginationRow = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PaginationText = styled.div`
  color: #64748b;
  font-size: 0.92rem;
  font-weight: 600;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.58);
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 980px;
  max-height: 90vh;
  overflow: auto;
  border-radius: 22px;
  background: white;
  box-shadow: 0 30px 70px rgba(15, 23, 42, 0.22);
`;

const PaymentModal = styled(ModalContent)`
  max-width: 560px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.4rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;

  h2 {
    margin: 0;
    color: #0f172a;
    font-size: 1.25rem;
  }

  p {
    margin: 0.3rem 0 0;
    color: #64748b;
  }
`;

const CloseButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.7rem 1rem;
  background: #f1f5f9;
  color: #334155;
  font-weight: 700;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const DetailCard = styled.div`
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 1rem;

  h3 {
    margin: 0 0 1rem;
    color: #0f172a;
    font-size: 1rem;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 0.55rem 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  span {
    color: #64748b;
    font-size: 0.92rem;
  }

  strong {
    color: #0f172a;
    text-align: right;
  }
`;

const FamilyBlock = styled.div`
  padding: 0.8rem;
  border-radius: 14px;
  background: white;
  border: 1px solid #e2e8f0;
  margin-bottom: 0.75rem;

  strong,
  span {
    display: block;
  }

  strong {
    color: #0f172a;
  }

  span {
    margin-top: 0.25rem;
    color: #64748b;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormStack = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.45rem;
    color: #334155;
    font-weight: 700;
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  font-size: 0.95rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  font-size: 0.95rem;
  resize: vertical;
`;

const PaymentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

export default CampRegistrationAdmin;
